"use client";

import { CheckCircledIcon, CrossCircledIcon } from "@radix-ui/react-icons";
import { Button, Flex, Text, Theme } from "@radix-ui/themes";
import Image from "next/image";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { env } from "next-runtime-env";
import { PropsWithChildren, Suspense, useEffect } from "react";
import { CommunicationCapabilities } from "@/clients/ntas/basic-selfcare";
import { AuthStatus, MyTN, Navbar } from "@/components";
import { AuthSessionValidator } from "@/components/AuthSessionValidator";
import CheckDefaultProfile from "@/components/CheckDefaultProfile";
import DisplayMyTN from "@/components/DisplayMyTN";
import DndBanner from "@/components/DndBanner";
import DndToggle from "@/components/DndToggle";
import AuthProvider from "@/providers/AuthProvider";
import QueryClientProvider from "@/providers/QueryClientProvider";
import { SaveWarningProvider } from "@/providers/SaveWarningProvider";
import { FlipBackArrowIcon, HelpCircle, VoiceCallerIcon } from "@/public/index";
import useCapabilitiesStore, { checkStoredBusinessAttendantCapability } from "@/stores/CapabilitiesStore";
import useTNStore from "@/stores/TNStore";
import useVFCCSessionStore, { VerificationState } from "@/stores/VFCCSessionStore";
import { HELP_URL } from "@/utils/constants";

export async function tnCapabilities(tn: string): Promise<{ communicationCapabilities: CommunicationCapabilities }> {
    const res = await fetch(`/api/v1/tns/${tn}/voice-settings/capabilities`);
    if (!res.ok) {
        return { communicationCapabilities: {} };
    }
    return res.json();
}

export default function ClientLayout({ children }: PropsWithChildren) {
    const { tn } = useTNStore();
    const { setCapabilities } = useCapabilitiesStore();
    const hasBusinessAttendantCapability = checkStoredBusinessAttendantCapability();
    const { vfccSession } = useVFCCSessionStore();

    const pathname: string = usePathname();
    const isSettingsRoute = pathname.includes("/account-settings");

    useEffect(() => {
        useCapabilitiesStore.persist.rehydrate();
    }, []);

    useEffect(() => {
        if (tn !== null && tn !== "") {
            tnCapabilities(tn)
                .then((data) => {
                    setCapabilities(data?.communicationCapabilities || {});
                })
                .catch();
        }
    }, [setCapabilities, tn]);

    return (
        <Suspense fallback={null}>
            <AuthProvider>
                <QueryClientProvider>
                    <Theme>
                        <header className="bg-white">
                                <Flex justify="between" align="center" wrap="wrap" className="px-4 min-h-[72px]">
                                    <Flex align="center" wrap="wrap">
                                        <Image src="/logo.png" alt="AT&T Config Portal" width="70" height="70" className="mr-2" priority={true} />
                                        <Text as="p" weight="medium" size="6" className="text-black">
                                            AT&T{" "}
                                            <Text as="span" weight="regular">
                                                Business Voice Settings
                                            </Text>
                                        </Text>
                                    </Flex>
                                    <Flex align="center" className="mr-4 gap-3.5" wrap="wrap">
                                        <MyTN />
                                        <Link
                                            href={env("NEXT_PUBLIC_BUSINESS_CENTER") || ""}
                                            className="rounded-full h-11 px-[9px] py-[11px] bg-att-light-blue text-sm font-medium text-att-cobalt-blue flex justify-center items-center gap-2.5">
                                            <FlipBackArrowIcon /> Back to Business Center home
                                        </Link>
                                        <Button
                                            onClick={() => window.open(env("NEXT_PUBLIC_HELP_URL") ?? HELP_URL, "_blank")}
                                            tabIndex={0}
                                            className="rounded-full cursor-pointer h-11 min-w-24 px-[9px] py-[11px] bg-att-light-blue text-sm font-medium text-att-cobalt-blue flex justify-center items-center gap-1">
                                            <HelpCircle />
                                            Help
                                        </Button>
                                        <AuthStatus />
                                    </Flex>
                                </Flex>
                                {isSettingsRoute ? (
                                    <Flex justify="between" align="center" wrap="wrap" className="px-4 min-h-[110px] border border-gray-200">
                                        <Flex direction="column">
                                            <Link href="/voice-settings" className="text-att-cobalt-blue text-base font-medium">
                                                &lt; Go back to console view
                                            </Link>
                                            <Text as="p" weight="bold" className="text-5xl">
                                                Account Settings
                                            </Text>
                                        </Flex>
                                    </Flex>
                                ) : (
                                    <Flex justify="between" align="center" wrap="wrap" className="px-4 min-h-[110px] border border-gray-200">
                                        <Flex align="center" wrap="wrap" className="gap-5">
                                            <VoiceCallerIcon />
                                            <Flex direction="column">
                                                <Flex align="center" className="gap-1">
                                                    <Text as="p" size="3" weight="medium" className="text-att-gray">
                                                        {vfccSession.tnInfo?.siteName || "Site 1"} &gt; {vfccSession.tnInfo?.alias || "Line 1"}
                                                        {vfccSession.tnInfo?.serviceApplication
                                                            ? ` - ${vfccSession.tnInfo?.serviceApplication}`
                                                            : vfccSession.tnInfo?.lineMode
                                                              ? ` - ${vfccSession.tnInfo?.lineMode}`
                                                              : hasBusinessAttendantCapability
                                                                ? " - Business Attendant"
                                                                : ""}
                                                    </Text>
                                                    {vfccSession.state === VerificationState.VERIFIED && (
                                                        <Text as="p" size="2" className="text-success">
                                                            <CheckCircledIcon />
                                                        </Text>
                                                    )}
                                                    {vfccSession.state === VerificationState.UNVERIFIED && (
                                                        <>
                                                            <Text as="p" size="2" className="text-att-red">
                                                                <CrossCircledIcon />
                                                            </Text>
                                                            <Text as="p" size="2" weight="regular" className="text-att-red">
                                                                unverified
                                                            </Text>
                                                        </>
                                                    )}
                                                </Flex>
                                                <DisplayMyTN />
                                            </Flex>
                                        </Flex>
                                        <Flex wrap="wrap" gap="2">
                                            <DndToggle />
                                        </Flex>
                                    </Flex>
                                )}
                                <DndBanner />
                                <CheckDefaultProfile />
                            </header>
                        <Flex className="flex-1 max-md:flex-wrap">
                            {!isSettingsRoute && <Navbar />}
                            <main className="mx-auto bg-white flex-1 min-h-[calc(100vh-228px)]">
                                <SaveWarningProvider>
                                    <AuthSessionValidator>{children}</AuthSessionValidator>
                                </SaveWarningProvider>
                            </main>
                        </Flex>
                    </Theme>
                </QueryClientProvider>
            </AuthProvider>
        </Suspense>
    );
}
